import type { ListedFork } from "@gearbox-protocol/deploy-tools-shared";
import { NetworkLogo } from "@gearbox-protocol/deploy-tools-ui";
import clsx from "clsx";
import ForkDropdownMenu from "components/fork-dropdown";
import { formatDistanceToNow } from "date-fns";
import type { FC, ReactNode } from "react";
import { Link } from "react-router-dom";

interface ForkListProps {
  title: ReactNode;
  forks: ListedFork[];
  className?: string;
}

const ForkList: FC<ForkListProps> = ({ forks, title, className }) => {
  return (
    <>
      {title}
      <ul className={clsx("list-disc pl-4", className)}>
        {forks.map(f => (
          <ForkListItem key={f.alias || f.executionId} {...f} />
        ))}
      </ul>
    </>
  );
};

const ForkListItem: FC<ListedFork> = props => {
  const {
    executionId,
    alias,
    isActive,
    persistent,
    network,
    createdAt,
    description,
    paused,
  } = props;

  const link = alias || executionId;
  let name: ReactNode;
  if (paused) {
    name = alias;
  } else if (isActive) {
    name = (
      <>
        <b>{alias}</b>
        {` (${executionId})`}
      </>
    );
  } else {
    name = (
      <>
        <b>{alias || executionId}</b>
        {executionId && alias ? ` (${executionId})` : ""}
      </>
    );
  }

  return (
    <li className="hover:bg-muted">
      <div className="flex flex-col p-1">
        <div className="flex flex-row items-center">
          <p>
            <span className="mr-1">
              {paused ? "⏸️" : persistent ? "📌" : ""}
            </span>

            <NetworkLogo network={network} className="mr-1" />

            <Link key={executionId} to={`/forks/${link}`}>
              <span className="ml-1">{name}</span>
            </Link>

            {!paused && createdAt && !createdAt.startsWith("0001-01-01") && (
              <span className="ml-1 text-muted-foreground text-sm">
                (
                {formatDistanceToNow(new Date(createdAt), {
                  addSuffix: true,
                })}
                )
              </span>
            )}
          </p>
          {(isActive || paused) && (
            <ForkDropdownMenu
              fork={props}
              variant="link"
              className="ml-2 hover:bg-slate-600"
            />
          )}
        </div>
        {!!description && (
          <p className="text-muted-foreground text-sm">{description}</p>
        )}
      </div>
    </li>
  );
};

export default ForkList;
